import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCourses } from '../hooks/useCourses';
import {
  AcademicCapIcon,
  PlayIcon,
  BookOpenIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  VideoCameraIcon,
  QuestionMarkCircleIcon,
  DocumentIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  LightBulbIcon,
  StarIcon,
  RocketLaunchIcon,
  TrophyIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { Course, CourseSection, CourseLesson, CourseBook } from '../types';
import LessonContent from '../components/LessonContent';
import { getCleanText } from '../utils/htmlRenderer';
import toast from 'react-hot-toast';
import { useClassrooms } from '../hooks/useClassrooms';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCourseProgress } from '../hooks/useCourseProgress';
import { AIService } from '../utils/aiService';
import CustomDropdown from '../components/CustomDropdown';
import AIChatButton from '../components/AIChatButton';

const CourseLearning: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { currentUser } = useAuth();
  const { courses, loading: coursesLoading } = useCourses();
  const navigate = useNavigate();

  // Track where user came from for proper back navigation
  const [previousPage, setPreviousPage] = useState<string>('/app/courses');

  const [course, setCourse] = useState<Course | null>(null);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [currentLesson, setCurrentLesson] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseClassrooms, setCourseClassrooms] = useState<{ id: string; name: string; imageUrl?: string | null; subject?: string }[]>([]);
  const { joinClassroom } = useClassrooms();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { saveLessonProgress, getOverallProgress, getLessonProgress, isLessonCompleted } = useCourseProgress(courseId || '');
  const [materialModal, setMaterialModal] = useState<{ title: string; url: string } | null>(null);

  // Course-level AI Assistant state
  const [showCourseAI, setShowCourseAI] = useState(false);
  const [aiModeCourse, setAiModeCourse] = useState<'summary' | 'quiz' | 'flashcards' | 'ask'>('summary');
  const [aiLoadingCourse, setAiLoadingCourse] = useState(false);
  const [aiErrorCourse, setAiErrorCourse] = useState<string>('');
  const [aiResultCourse, setAiResultCourse] = useState<any>(null);
  const [aiQuestionCourse, setAiQuestionCourse] = useState<string>('');
  const [isFocusMode, setIsFocusMode] = useState(false);

  const getCourseAIContext = (): string => {
    if (!course) return '';
    const parts: string[] = [];
    parts.push(`Course: ${getCleanText(course.name)}`);
    if (course.description) parts.push(`Overview: ${getCleanText(course.description)}`);
    if (Array.isArray(course.sections) && course.sections.length > 0) {
      const sectionsStr = course.sections
        .map((s, si) => {
          if (typeof s === 'string') return `Section ${si + 1}: ${s}`;
          const lessons = (s.lessons || [])
            .map((l, li) => (typeof l === 'string' ? `- Lesson ${li + 1}: ${l}` : `- Lesson ${li + 1}: ${getCleanText(l.name)}`))
            .join('\n');
          return `Section ${si + 1}: ${getCleanText(s.name)}\n${lessons}`;
        })
        .join('\n');
      parts.push(`Content Structure:\n${sectionsStr}`);
    }
    if (Array.isArray(course.books) && course.books.length > 0) {
      const booksStr = course.books
        .map((b, bi) => {
          const bk: any = typeof b === 'string' ? { title: b } : b;
          return `${bi + 1}. ${bk.title}${bk.author ? ` by ${bk.author}` : ''}`;
        })
        .join('\n');
      parts.push(`Resources (Books):\n${booksStr}`);
    }
    if (course.duration) parts.push(`Duration: ${course.duration}`);
    return parts.join('\n\n');
  };

  const runCourseAI = async () => {
    setAiLoadingCourse(true);
    setAiErrorCourse('');
    setAiResultCourse(null);
    try {
      const ai = AIService.getInstance();
      const ctx = getCourseAIContext();
      if (aiModeCourse === 'ask') {
        const response = await ai.processChatMessage('', (aiQuestionCourse?.trim() ? aiQuestionCourse : 'Explain this course overview.') + `\n\nContext:\n${ctx}`, {
          courseId: courseId || '',
          lessonId: '',
          lessonTitle: 'Course Overview',
          lessonText: ctx
        });
        setAiResultCourse({ type: 'ask', ...response });
      } else {
        const type = aiModeCourse; // 'summary' | 'quiz' | 'flashcards'
        const result = await ai.generateContent(courseId || '', 'course_overview', type, ctx);
        setAiResultCourse(result);
      }
    } catch (e: any) {
      setAiErrorCourse(e?.message || 'AI request failed. Please try again.');
    } finally {
      setAiLoadingCourse(false);
    }
  };

  // Detect where user came from for proper back navigation
  useEffect(() => {
    const referrer = document.referrer;
    const currentPath = window.location.pathname;

    // Check if user came from MyCourses page
    if (referrer.includes('/app/courses') || referrer.includes('/my-courses')) {
      setPreviousPage('/app/courses');
    } else if (referrer.includes('/app/dashboard') || referrer.includes('/dashboard')) {
      setPreviousPage('/app/dashboard');
    } else if (referrer.includes('/app/catalog') || referrer.includes('/catalog')) {
      setPreviousPage('/app/catalog');
    } else {
      // Try to use browser history as fallback
      if (window.history.length > 1) {
        // If we have history, we can go back
        setPreviousPage('back');
      } else {
        // Default to MyCourses if we can't determine the source
        setPreviousPage('/app/courses');
      }
    }


  }, []);

  // Fetch course data
  useEffect(() => {
    if (!courseId) {
      setError('Course ID is required');
      setLoading(false);
      return;
    }

    if (courses.length === 0) {
      return; // Keep loading state while courses are being fetched
    }

    const foundCourse = courses.find(c => c.id === courseId);

    // Check if course is expired
    if (foundCourse && 'expire_date' in foundCourse && foundCourse.expire_date) {
      const now = new Date();
      const expiry = new Date(foundCourse.expire_date as string);
      if (expiry < now) {
        setError('This course has expired. Please add it to your cart to continue learning.');
        setLoading(false);
        return;
      }
    }

    if (foundCourse) {
      setCourse(foundCourse);
      // Expand first section by default
      if (foundCourse.sections && foundCourse.sections.length > 0) {
        const firstSection = foundCourse.sections[0];
        const firstSectionId = typeof firstSection === 'string' ? firstSection : firstSection.id;
        setExpandedSections(new Set([firstSectionId]));
        setCurrentSection(firstSectionId);

        // Auto-select first lesson if available
        if (typeof firstSection !== 'string' && firstSection.lessons && firstSection.lessons.length > 0) {
          const firstLesson = firstSection.lessons[0];
          const firstLessonId = typeof firstLesson === 'string' ? firstLesson : firstLesson.id;
          setCurrentLesson(firstLessonId);
        }
      }
    } else {
      setError('Course not found');
    }
    setLoading(false);
  }, [courseId, courses]);

  // Fetch classrooms for this course from subcollection 'Classrooms'
  useEffect(() => {
    let isMounted = true;
    const fetchClassrooms = async () => {
      if (!courseId) return;
      try {
        const classroomsRef = collection(db, 'courses', courseId, 'Classrooms');
        const snapshot = await getDocs(classroomsRef);
        if (snapshot.empty) {
          if (isMounted) setCourseClassrooms([]);
          return;
        }
        const idSet = new Set<string>();
        const inline: { id: string; name: string }[] = [];
        snapshot.forEach((cDoc) => {
          const d: any = cDoc.data();
          if (typeof d.classroomName === 'string' && d.classroomName.trim()) {
            inline.push({ id: cDoc.id, name: d.classroomName });
          }
          const raw = d.classroomId;
          if (Array.isArray(raw)) {
            raw.forEach((item) => {
              if (typeof item === 'string') idSet.add(item);
              else if (item && typeof item === 'object') {
                if (typeof item.id === 'string') idSet.add(item.id);
                else if (typeof item.classroomId === 'string') idSet.add(item.classroomId);
              }
            });
          } else if (typeof raw === 'string') {
            idSet.add(raw);
          } else if (raw && typeof raw === 'object') {
            if (typeof raw.id === 'string') idSet.add(raw.id);
            else if (typeof raw.classroomId === 'string') idSet.add(raw.classroomId);
          }
        });
        const resolved = await Promise.all(
          Array.from(idSet).map(async (cid) => {
            try {
              const cSnap = await getDoc(doc(db, 'classrooms', cid));
              if (cSnap.exists()) {
                const data: any = cSnap.data();
                return { id: cSnap.id, name: data.name || data.classroomName || cSnap.id, imageUrl: data.profileImageUrl || null, subject: data.subject || '' };
              }
            } catch { }
            return null;
          })
        );
        const cleaned = [...inline, ...(resolved.filter(Boolean) as { id: string; name: string; imageUrl?: string | null; subject?: string }[])];
        // Deduplicate
        const dedup = Array.from(new Map(cleaned.map(c => [c.id, c])).values());
        if (isMounted) setCourseClassrooms(dedup);
      } catch { }
    };
    fetchClassrooms();
    return () => { isMounted = false; };
  }, [courseId]);

  const handleEnterClassroom = async (classroomId: string) => {
    try {
      await joinClassroom(classroomId);
      toast.success('Entering classroom...');
      navigate(`/app/classrooms/${classroomId}`);
    } catch (e) {
      // Even if join fails due to already a member, still navigate in case access is allowed
      navigate(`/app/classrooms/${classroomId}`);
    }
  };

  // Navigation helpers for mobile bottom bar
  const goToPrevLesson = () => {
    if (!course || !currentLesson || !course.sections) return;
    let prevLessonId: string | null = null;
    for (let s = 0; s < course.sections.length; s++) {
      const section = course.sections[s];
      if (typeof section !== 'string' && section.lessons) {
        const idx = section.lessons.findIndex(l => (typeof l === 'string' ? l : l.id) === currentLesson);
        if (idx !== -1) {
          if (idx > 0) {
            const prev = section.lessons[idx - 1];
            prevLessonId = typeof prev === 'string' ? prev : prev.id;
          } else if (s > 0) {
            const prevSection = course.sections[s - 1];
            if (typeof prevSection !== 'string' && prevSection.lessons && prevSection.lessons.length > 0) {
              const last = prevSection.lessons[prevSection.lessons.length - 1];
              prevLessonId = typeof last === 'string' ? last : last.id;
            }
          }
          break;
        }
      }
    }
    if (prevLessonId) selectLesson(prevLessonId);
  };

  const goToNextLesson = () => {
    if (!course || !currentLesson || !course.sections) return;
    let nextLessonId: string | null = null;
    for (let s = 0; s < course.sections.length; s++) {
      const section = course.sections[s];
      if (typeof section !== 'string' && section.lessons) {
        const idx = section.lessons.findIndex(l => (typeof l === 'string' ? l : l.id) === currentLesson);
        if (idx !== -1) {
          if (idx < (section.lessons.length - 1)) {
            const next = section.lessons[idx + 1];
            nextLessonId = typeof next === 'string' ? next : next.id;
          } else if (s < (course.sections.length - 1)) {
            const nextSection = course.sections[s + 1];
            if (typeof nextSection !== 'string' && nextSection.lessons && nextSection.lessons.length > 0) {
              const first = nextSection.lessons[0];
              nextLessonId = typeof first === 'string' ? first : first.id;
            }
          }
          break;
        }
      }
    }
    if (nextLessonId) selectLesson(nextLessonId);
  };

  // Derived progress values
  const overallProgress = getOverallProgress();
  const totalSections = course?.sections ? course.sections.length : 0;
  const totalLessons = (() => {
    if (!course?.sections) return 0;
    let total = 0;
    course.sections.forEach((s) => {
      if (typeof s !== 'string' && s.lessons) total += s.lessons.length;
    });
    return total;
  })();
  const completedLessons = (() => {
    if (!course?.sections) return 0;
    let count = 0;
    course.sections.forEach((s) => {
      if (typeof s !== 'string' && s.lessons) {
        s.lessons.forEach((l) => {
          const id = typeof l === 'string' ? l : l.id;
          if (isLessonCompleted(id)) count += 1;
        });
      }
    });
    return count;
  })();

  // Build preview URL for learning materials (PDF/Drive)
  const getMaterialPreviewUrl = (rawUrl: string): string => {
    if (!rawUrl) return '';
    const url = rawUrl;
    if (url.includes('drive.google.com')) {
      const idMatch = url.match(/\/file\/d\/([^/]+)/) || url.match(/open\?id=([^&]+)/) || url.match(/uc\?id=([^&]+)/);
      const fileId = idMatch ? idMatch[1] : null;
      return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : url;
    }
    // Hide default toolbar for PDF viewers where supported
    if (url.toLowerCase().endsWith('.pdf')) {
      return `${url}#toolbar=0&navpanes=0`;
    }
    return url;
  };

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // Handle lesson selection
  const selectLesson = (lessonId: string) => {
    setCurrentLesson(lessonId);
    try {
      const existing = getLessonProgress(lessonId);
      if (!existing || !existing.completed) {
        saveLessonProgress(lessonId, Math.max(existing?.progress || 0, 1));
      }
    } catch { }
  };

  // Get section name
  const getSectionName = (section: string | CourseSection): string => {
    return typeof section === 'string' ? `Section ${section}` : section.name;
  };

  // Get lesson name
  const getLessonName = (lesson: string | CourseLesson): string => {
    return typeof lesson === 'string' ? `Lesson ${lesson}` : lesson.name;
  };

  // Get lesson content type icon
  const getLessonIcon = (lesson: string | CourseLesson) => {
    if (typeof lesson === 'string') return <DocumentIcon className="h-4 w-4" />;

    const contentType = lesson.content_type?.toLowerCase();
    if (contentType?.includes('video')) return <VideoCameraIcon className="h-4 w-4" />;
    if (contentType?.includes('quiz')) return <QuestionMarkCircleIcon className="h-4 w-4" />;
    if (contentType?.includes('article')) return <DocumentTextIcon className="h-4 w-4" />;
    return <DocumentIcon className="h-4 w-4" />;
  };

  if (loading || coursesLoading) {

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1">
                <div className="h-96 bg-gray-200 rounded"></div>
              </div>
              <div className="lg:col-span-3">
                <div className="h-96 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    const isExpiredCourse = error && error.includes('expired');

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {isExpiredCourse ? 'Course Expired' : 'Course Not Found'}
            </h1>
            <p className="text-gray-600 mb-6">{error || 'The requested course could not be found.'}</p>

            {isExpiredCourse ? (
              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Add to cart logic here
                  }}
                  className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <AcademicCapIcon className="h-5 w-5 mr-2" />
                  Add to Cart
                </button>
                <div className="text-sm text-gray-500">
                  Add this course to your cart to continue learning
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {previousPage === 'back' ? (
                  <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Go Back
                  </button>
                ) : (
                  <Link
                    to={previousPage}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to {(() => {
                      if (previousPage.includes('/courses')) return 'My Courses';
                      if (previousPage.includes('/dashboard')) return 'Dashboard';
                      return 'Previous Page';
                    })()}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            {/* Left - Text */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Back control */}
              <div className="flex items-center justify-center lg:justify-start">
                {previousPage === 'back' ? (
                  <button onClick={() => navigate(-1)} className="inline-flex items-center px-3 py-1.5 bg-white/10 border border-white/20 text-white rounded-md text-xs hover:bg-white/20">
                    <ArrowLeftIcon className="h-4 w-4 mr-1.5" /> Back
                  </button>
                ) : (
                  <Link to={previousPage} className="inline-flex items-center px-3 py-1.5 bg-white/10 border border-white/20 text-white rounded-md text-xs hover:bg-white/20">
                    <ArrowLeftIcon className="h-4 w-4 mr-1.5" /> Back
                  </Link>
                )}
              </div>

              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <BookOpenIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Learning Dashboard
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white mb-2">
                <span className="block break-words line-clamp-2">{getCleanText(course.name)}</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Course Overview
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Track progress, access lessons and resources, and keep your momentum going.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{totalSections}</div>
                  <div className="text-xs text-blue-200">Sections</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{totalLessons || course.lessons_count || 0}</div>
                  <div className="text-xs text-blue-200">Lessons</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{course.books ? course.books.length : 0}</div>
                  <div className="text-xs text-blue-200">Resources</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{overallProgress}%</div>
                  <div className="text-xs text-blue-200">Complete</div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => { document.getElementById('learning-content-top')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" /> Resume Learning
                </button>
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <SparklesIcon className="h-4 w-4 mr-1.5" /> Course Contents
                </button>
              </div>
            </div>

            {/* Right - Visuals (desktop only) */}
            <div className="relative hidden xl:block">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <PlayIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Active Lesson</h3>
                      <p className="text-blue-200 text-sm">Keep going</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Achievements</h3>
                      <p className="text-blue-200 text-sm">{overallProgress}% complete</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main content */}
      <div id="learning-content-top" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-6">
          {/* Course Navigation Sidebar */}
          <div className={isFocusMode ? "hidden" : "lg:col-span-3"}>
            {/* Mobile Off-canvas */}
            {isSidebarOpen && (
              <div className="fixed inset-0 z-40 md:hidden">
                <div className="absolute inset-0 bg-black/40" onClick={() => setIsSidebarOpen(false)}></div>
                <div className="absolute top-0 left-0 h-full w-5/6 max-w-xs bg-white shadow-xl p-4 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Contents</h2>
                    <button onClick={() => setIsSidebarOpen(false)} className="text-gray-600 hover:text-gray-900">Close</button>
                  </div>
                  {/* Sidebar content duplicated for mobile */}
                  <div className="space-y-4">
                    {/* Course Progress */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Overall Progress</span>
                        <span>{overallProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${overallProgress}%` }}></div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                        <span>{completedLessons} of {totalLessons || course.lessons_count || 0} lessons</span>
                        <span>{totalSections} sections</span>
                      </div>
                    </div>

                    {/* Course Sections */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Course Content</h3>
                      {course.sections && course.sections.length > 0 ? (
                        course.sections.map((section, sectionIndex) => {
                          const sectionId = typeof section === 'string' ? section : section.id;
                          const isExpanded = expandedSections.has(sectionId);
                          const sectionName = getSectionName(section);
                          const lessonCount = typeof section !== 'string' && section.lessons ? section.lessons.length : 0;
                          return (
                            <div key={`m-${sectionId}`} className="border border-gray-200 rounded-lg">
                              <button onClick={() => toggleSection(sectionId)} className="w-full p-3 text-left flex items-center justify-between hover:bg-gray-50">
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                  <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium">{sectionIndex + 1}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-sm truncate" title={sectionName}>{sectionName}</div>
                                    <div className="text-xs text-gray-500">{lessonCount} lesson{lessonCount !== 1 ? 's' : ''}</div>
                                  </div>
                                </div>
                                {isExpanded ? <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                              </button>
                              {isExpanded && typeof section !== 'string' && section.lessons && (
                                <div className="border-t border-gray-200 p-2 space-y-1">
                                  {section.lessons.map((lesson, lessonIndex) => {
                                    const lessonId = typeof lesson === 'string' ? lesson : lesson.id;
                                    const lessonName = getLessonName(lesson);
                                    const isActive = currentLesson === lessonId;
                                    return (
                                      <button
                                        key={`m-${lessonId}`}
                                        onClick={() => { selectLesson(lessonId); setIsSidebarOpen(false); }}
                                        className={`w-full p-2 text-left text-sm rounded flex items-center space-x-2 transition-colors ${isActive ? 'bg-primary-100 text-primary-700 border border-primary-200' : 'text-gray-600 hover:bg-gray-100'}`}
                                      >
                                        <div className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium">{lessonIndex + 1}</div>
                                        <span className="truncate flex-1 max-w-[200px]" title={lessonName}>{lessonName}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-sm text-gray-500">No course content available yet</div>
                      )}
                    </div>

                    {/* Learning Resources (Books) */}
                    {course.books && course.books.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Learning Resources</h3>
                        {course.books.map((book, index) => {
                          const bookData = typeof book === 'string' ? null : book;
                          return (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <BookOpenIcon className="h-4 w-4 text-blue-600" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {bookData ? bookData.title : `Book ${index + 1}`}
                                  </p>
                                  {bookData && (
                                    <p className="text-xs text-gray-600 truncate">
                                      {bookData.author || 'Unknown Author'}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {bookData && (
                                <div className="mt-2 flex items-center gap-2">
                                  {bookData.pdf_url && (
                                    <button
                                      onClick={() => {
                                        const openId = encodeURIComponent(bookData.id);
                                        const openTitle = encodeURIComponent(bookData.title);
                                        const openUrl = encodeURIComponent(bookData.pdf_url);
                                        const returnTo = encodeURIComponent(`/app/course-learning/${courseId}`);
                                        navigate(`/app/books?openId=${openId}&openTitle=${openTitle}&openUrl=${openUrl}&returnTo=${returnTo}`);
                                        setIsSidebarOpen(false);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
                                    >
                                      Open Material
                                    </button>
                                  )}
                                  {!bookData.pdf_url && (
                                    <span className="text-xs text-gray-500">No file available</span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Classroom Section */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-orange-600 mr-2" />

                      </h3>
                      {courseClassrooms.length > 0 ? (
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {courseClassrooms.slice(0, 3).map((c) => (
                              <span key={c.id} className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-xs">
                                {c.name}
                              </span>
                            ))}
                            {courseClassrooms.length > 3 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200 text-xs">+{courseClassrooms.length - 3} more</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { handleEnterClassroom(courseClassrooms[0].id); setIsSidebarOpen(false); }}
                              className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs hover:bg-orange-700"
                              title="Join this classroom and add it to your Classrooms page"
                            >
                              Join & Add
                            </button>
                            <Link to={`/app/classrooms/${courseClassrooms[0].id}`} className="text-xs text-orange-700 hover:text-orange-800">
                              Open
                            </Link>
                          </div>
                          <p className="text-xs text-gray-500">Enter the classroom to collaborate with peers for this course.</p>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                          Classroom is available for this course. Enter to participate in discussions and live sessions.
                          <div className="mt-2 flex items-center gap-2">
                            <Link to="/app/classrooms" className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs hover:bg-orange-700">
                              View Classrooms
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="hidden md:block bg-white rounded-lg border border-gray-200 p-4 lg:sticky lg:top-4 h-fit">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <AcademicCapIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Course Content</h2>
                  <p className="text-xs text-gray-500 line-clamp-1">{getCleanText(course.name)}</p>
                </div>
              </div>

              {/* Course Progress */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Overall Progress</span>
                  <span>{overallProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${overallProgress}%` }}></div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                  <span>{completedLessons} of {totalLessons || course.lessons_count || 0} lessons</span>
                  <span>{totalSections} sections</span>
                </div>
              </div>

              {/* Sections Navigation */}
              {course.sections && course.sections.length > 0 ? (
                <div className="space-y-2">
                  {course.sections.map((section, sectionIndex) => {
                    const sectionId = typeof section === 'string' ? section : section.id;
                    const isExpanded = expandedSections.has(sectionId);
                    const sectionName = getSectionName(section);
                    const lessonCount = typeof section !== 'string' && section.lessons ? section.lessons.length : 0;

                    return (
                      <div key={sectionId} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleSection(sectionId)}
                          className="w-full p-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-medium">
                              {sectionIndex + 1}
                            </div>
                            <div className="text-left flex-1 min-w-0">
                              <div className="font-medium text-gray-900 " title={sectionName}>{sectionName}</div>
                              <div className="text-xs text-gray-500">{lessonCount} lesson{lessonCount !== 1 ? 's' : ''}</div>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                          )}
                        </button>

                        {isExpanded && typeof section !== 'string' && section.lessons && (
                          <div className="border-t border-gray-200 p-2 space-y-1">
                            {section.lessons.map((lesson, lessonIndex) => {
                              const lessonId = typeof lesson === 'string' ? lesson : lesson.id;
                              const lessonName = getLessonName(lesson);
                              const isActive = currentLesson === lessonId;
                              const lessonType = typeof lesson === 'string' ? 'text' : lesson.content_type;

                              return (
                                <button
                                  key={lessonId}
                                  onClick={() => selectLesson(lessonId)}
                                  className={`w-full p-2 text-left text-sm rounded flex items-center space-x-2 transition-colors ${isActive
                                    ? 'bg-primary-100 text-primary-700 border border-primary-200'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <div className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium">
                                      {lessonIndex + 1}
                                    </div>
                                    <div className="flex-shrink-0">{getLessonIcon(lesson)}</div>
                                    <span className="truncate flex-1 max-w-[160px] sm:max-w-[200px]" title={lessonName}>{lessonName}</span>

                                  </div>
                                  {lessonType && (
                                    <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${lessonType.includes('video') ? 'bg-green-100 text-green-800' :
                                      lessonType.includes('quiz') ? 'bg-purple-100 text-purple-800' :
                                        'bg-blue-100 text-blue-800'
                                      }`}>
                                      {lessonType}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No course content available yet</p>
                  <p className="text-xs text-gray-400">Course sections and lessons will appear here</p>
                </div>
              )}

              {/* Books Section */}
              {course.books && course.books.length > 0 ? (
                <div className="mt-6">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">Learning Resources</h3>
                  <div className="space-y-2">
                    {course.books.map((book, index) => {
                      const bookData = typeof book === 'string' ? null : book;
                      return (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <BookOpenIcon className="h-4 w-4 text-blue-600" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {bookData ? bookData.title : `Book ${index + 1}`}
                              </p>
                              {bookData && (
                                <p className="text-xs text-gray-600 truncate">
                                  {bookData.author || 'Unknown Author'}
                                </p>
                              )}
                            </div>
                          </div>
                          {bookData && (
                            <div className="mt-2 flex items-center gap-2">
                              {bookData.pdf_url && (
                                <button
                                  onClick={() => {
                                    const openId = encodeURIComponent(bookData.id);
                                    const openTitle = encodeURIComponent(bookData.title);
                                    const openUrl = encodeURIComponent(bookData.pdf_url);
                                    const returnTo = encodeURIComponent(`/app/course-learning/${courseId}`);
                                    navigate(`/app/books?openId=${openId}&openTitle=${openTitle}&openUrl=${openUrl}&returnTo=${returnTo}`);
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
                                >
                                  Open Material
                                </button>
                              )}
                              {!bookData.pdf_url && (
                                <span className="text-xs text-gray-500">No file available</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mt-6 text-center py-4 text-gray-500">
                  <BookOpenIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No learning resources available</p>
                  <p className="text-xs text-gray-400">Books and materials will appear here</p>
                </div>
              )}

              {/* Classroom Section */}
              <div className="mt-6">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-orange-600 mr-2" />
                  Classroom
                </h3>
                {courseClassrooms.length > 0 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {courseClassrooms.slice(0, 4).map((c) => (
                        <div key={c.id} className="flex items-center p-2 bg-white rounded-md border border-gray-200">
                          <div className="w-8 h-8 rounded-md bg-gray-100 overflow-hidden flex-shrink-0 mr-2">
                            {c.imageUrl ? (
                              <img src={c.imageUrl} alt={c.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-500">
                                {c.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate">{c.name}</div>
                            {c.subject ? <div className="text-[10px] text-gray-500 truncate">{c.subject}</div> : null}
                          </div>
                          <button
                            onClick={() => { handleEnterClassroom(c.id); setIsSidebarOpen(false); }}
                            className="ml-2 text-[10px] px-2 py-1 rounded bg-orange-600 text-white hover:bg-orange-700"
                            title="Join this classroom"
                          >
                            Join
                          </button>
                        </div>
                      ))}
                      {courseClassrooms.length > 4 && (
                        <div className="text-[11px] text-gray-600">+{courseClassrooms.length - 4} more</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEnterClassroom(courseClassrooms[0].id)}
                        className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs hover:bg-orange-700"
                        title="Join this classroom and add it to your Classrooms page"
                      >
                        Join & Add
                      </button>
                      <Link to={`/app/classrooms/${courseClassrooms[0].id}`} className="text-xs text-orange-700 hover:text-orange-800">
                        Open
                      </Link>
                    </div>
                    <p className="text-xs text-gray-500">Enter the classroom to collaborate with peers for this course.</p>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                    Classroom is available for this course. Click below to add it to your Classrooms and join.
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => { if (courseClassrooms.length > 0) handleEnterClassroom(courseClassrooms[0].id); }}
                        className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs hover:bg-orange-700"
                      >
                        Add & Join Classroom
                      </button>
                      <Link to="/app/classrooms" className="text-orange-700 hover:text-orange-800">View All</Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Course Overview and Lesson Content */}
          <div className={isFocusMode ? "lg:col-span-12" : "lg:col-span-9"}>
            {/* Course Overview Panel */}
            <div className="mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
                {/* Profstudymate on top of Course Overview */}
                <div className="mb-4 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                        <SparklesIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="text-base md:text-lg font-bold text-gray-900">Profstudymate</div>
                        <div className="text-xs md:text-sm text-gray-600">Summaries, quizzes, flashcards, or ask anything about this course</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowCourseAI(!showCourseAI)}
                      className={`px-3 py-1.5 text-sm rounded-md font-medium ${showCourseAI ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}
                    >
                      {showCourseAI ? 'Hide' : 'Open'}
                    </button>
                  </div>

                  {showCourseAI && (
                    <div className="mt-4 space-y-3">
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                        <div className="lg:col-span-3 bg-white rounded-md border border-gray-200 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-semibold text-gray-800 flex items-center">
                              <LightBulbIcon className="h-4 w-4 text-blue-600 mr-2" /> Choose AI Task
                            </label>
                            <CustomDropdown
                              value={aiModeCourse}
                              onChange={(value) => setAiModeCourse(value as any)}
                              options={[
                                { value: "summary", label: "Summary" },
                                { value: "quiz", label: "Generate Quiz" },
                                { value: "flashcards", label: "Flashcards" },
                                { value: "ask", label: "Ask a Question" }
                              ]}
                              placeholder="Select AI Task"
                              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                            />
                          </div>
                          {aiModeCourse === 'ask' && (
                            <textarea
                              value={aiQuestionCourse}
                              onChange={e => setAiQuestionCourse(e.target.value)}
                              placeholder="Ask anything about this course..."
                              className="w-full mt-2 p-2 text-sm border border-gray-300 rounded-md min-h-[80px]"
                            />
                          )}
                          <div className="mt-3">
                            <button
                              onClick={runCourseAI}
                              disabled={aiLoadingCourse || (aiModeCourse === 'ask' && !aiQuestionCourse.trim())}
                              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold ${aiLoadingCourse ? 'bg-gray-300 text-gray-600' : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'}`}
                            >
                              {aiLoadingCourse ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <SparklesIcon className="h-4 w-4 mr-2" /> Generate
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="bg-white rounded-md border border-gray-200 p-3">
                          <div className="text-xs text-gray-600 mb-2">Context used</div>
                          <div className="text-xs text-gray-800 max-h-28 overflow-y-auto border rounded p-2 bg-gray-50 whitespace-pre-wrap">
                            {getCourseAIContext().slice(0, 300) || 'No text content available'}
                          </div>
                        </div>
                      </div>

                      {aiErrorCourse && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">{aiErrorCourse}</div>
                      )}

                      {aiResultCourse && (
                        <div className="bg-white border border-gray-200 rounded-md p-4">
                          {aiResultCourse.type === 'summary' && aiResultCourse.content && (
                            <div className="space-y-3">
                              <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                                <span>AI Summary</span>
                              </div>
                              {aiResultCourse.content.summary && (
                                <p className="text-gray-800 text-sm leading-relaxed">{aiResultCourse.content.summary}</p>
                              )}
                              {aiResultCourse.content.keyPoints && Array.isArray(aiResultCourse.content.keyPoints) && (
                                <div>
                                  <div className="text-sm font-semibold mb-1">Key Points</div>
                                  <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1">
                                    {aiResultCourse.content.keyPoints.map((p: string, idx: number) => (
                                      <li key={idx}>{p}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}

                          {aiResultCourse.type === 'quiz' && aiResultCourse.content && aiResultCourse.content.questions && (
                            <div className="space-y-3">
                              <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                                <QuestionMarkCircleIcon className="h-5 w-5 text-purple-600" />
                                <span>AI-Generated Quiz</span>
                              </div>
                              <div className="space-y-3">
                                {aiResultCourse.content.questions.map((q: any, idx: number) => (
                                  <div key={idx} className="border border-gray-200 rounded-md p-3">
                                    <div className="text-sm font-medium text-gray-900 mb-2">{idx + 1}. {q.question}</div>
                                    <ul className="space-y-1">
                                      {q.options?.map((opt: string, i: number) => (
                                        <li key={i} className={`text-sm ${i === q.correctAnswer ? 'text-green-700' : 'text-gray-700'}`}>• {opt}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {aiResultCourse.type === 'flashcards' && aiResultCourse.content && aiResultCourse.content.cards && (
                            <div className="space-y-3">
                              <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                                <StarIcon className="h-5 w-5 text-yellow-600" />
                                <span>AI Flashcards</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {aiResultCourse.content.cards.map((c: any, idx: number) => (
                                  <div key={idx} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                    <div className="text-sm font-semibold text-gray-900">Q: {c.front}</div>
                                    <div className="text-sm text-gray-700 mt-1">A: {c.back}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {aiResultCourse.type === 'ask' && (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                                <LightBulbIcon className="h-5 w-5 text-green-600" />
                                <span>AI Answer</span>
                              </div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">{aiResultCourse.response}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Course Overview</h2>
                    <div className="mb-3">
                      <div className={`text-gray-600 prose prose-sm max-w-none ${!overviewExpanded ? 'line-clamp-3' : ''}`} style={{
                        display: '-webkit-box',
                        WebkitLineClamp: overviewExpanded ? 'unset' : 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {getCleanText(course.description)}
                      </div>
                      {course.description && course.description.length > 150 && (
                        <button
                          onClick={() => setOverviewExpanded(!overviewExpanded)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2 flex items-center"
                        >
                          {overviewExpanded ? (
                            <>
                              Show Less
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </>
                          ) : (
                            <>
                              Show More
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Course Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-xl font-bold text-primary-600">
                          {course.sections ? course.sections.length : 0}
                        </div>
                        <div className="text-xs text-gray-600">Sections</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-xl font-bold text-blue-600">
                          {course.lessons_count || 0}
                        </div>
                        <div className="text-xs text-gray-600">Lessons</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-xl font-bold text-green-600">
                          {course.books ? course.books.length : 0}
                        </div>
                        <div className="text-xs text-gray-600">Resources</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-xl font-bold text-purple-600">
                          {course.duration || 'Ongoing'}
                        </div>
                        <div className="text-xs text-gray-600">Duration</div>
                      </div>
                    </div>
                  </div>

                  {/* Course Progress */}
                  <div className="ml-4 md:ml-6 text-center">
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center mb-2"
                      style={{
                        background: `conic-gradient(#10b981 ${overallProgress * 3.6}deg, #e5e7eb 0deg)`
                      }}
                    >
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-700">{overallProgress}%</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">Complete</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Learning Content */}
            <div>
              <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 lg:p-6">
                {/* Breadcrumb Navigation */}
                <div className="mb-4 md:mb-6 pb-3 md:pb-4 border-b border-gray-200">
                  <nav className="flex items-center space-x-2 text-sm text-gray-500">
                    <Link to="/app/dashboard" className="hover:text-gray-700">
                      Dashboard
                    </Link>
                    <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-gray-900 font-medium truncate max-w-[80px] sm:max-w-[180px]">{getCleanText(course.name)}</span>
                    {currentSection && (
                      <>
                        <ChevronRightIcon className="h-4 w-4" />
                        <span className="text-gray-600">
                          {(() => {
                            const foundSection = course.sections?.find(s =>
                              (typeof s === 'string' ? s : s.id) === currentSection
                            );
                            if (foundSection) {
                              if (typeof foundSection === 'string') {
                                return `Section ${course.sections.findIndex(s => (typeof s === 'string' ? s : s.id) === currentSection) + 1}`;
                              } else {
                                return foundSection.name;
                              }
                            }
                            return 'Section';
                          })()}
                        </span>
                      </>
                    )}
                    {currentLesson && (
                      <>
                        <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-gray-600 truncate max-w-[80px] sm:max-w-[180px]">
                          {(() => {
                            let lessonName = 'Lesson';
                            if (course.sections) {
                              for (const section of course.sections) {
                                if (typeof section !== 'string' && section.lessons) {
                                  const foundLesson = section.lessons.find(l =>
                                    (typeof l === 'string' ? l : l.id) === currentLesson
                                  );
                                  if (foundLesson) {
                                    lessonName = typeof foundLesson === 'string' ? foundLesson : foundLesson.name;
                                    break;
                                  }
                                }
                              }
                            }
                            return lessonName;
                          })()}
                        </span>
                      </>
                    )}
                  </nav>
                </div>

                {currentLesson ? (
                  <div>
                    {(() => {
                      // Find the current lesson data
                      let lessonData: CourseLesson | null = null;
                      let currentSectionData: CourseSection | null = null;
                      let currentLessonIndex = -1;
                      let currentSectionIndex = -1;

                      // Find the section containing the current lesson
                      if (course.sections) {
                        for (let sectionIndex = 0; sectionIndex < course.sections.length; sectionIndex++) {
                          const section = course.sections[sectionIndex];
                          if (typeof section !== 'string' && section.lessons) {
                            const lessonIndex = section.lessons.findIndex(l =>
                              (typeof l === 'string' ? l : l.id) === currentLesson
                            );
                            if (lessonIndex !== -1) {
                              lessonData = typeof section.lessons[lessonIndex] === 'string' ? null : section.lessons[lessonIndex];
                              currentSectionData = section;
                              currentLessonIndex = lessonIndex;
                              currentSectionIndex = sectionIndex;
                              break;
                            }
                          }
                        }
                      }

                      if (lessonData) {
                        return (
                          <>
                            {/* Lesson Navigation Bar */}
                            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-primary-50 px-4 to-secondary-50 rounded-lg sticky top-0 z-30 shadow-sm border border-gray-100">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                  <button
                                    onClick={() => {
                                      // Navigate to previous lesson
                                      if (currentSectionData && currentLessonIndex > 0) {
                                        const prevLesson = currentSectionData.lessons[currentLessonIndex - 1];
                                        const prevLessonId = typeof prevLesson === 'string' ? prevLesson : prevLesson.id;
                                        selectLesson(prevLessonId);
                                      } else if (currentSectionIndex > 0) {
                                        // Navigate to previous section's last lesson
                                        const prevSection = course.sections[currentSectionIndex - 1];
                                        if (typeof prevSection !== 'string' && prevSection.lessons && prevSection.lessons.length > 0) {
                                          const lastLesson = prevSection.lessons[prevSection.lessons.length - 1];
                                          const lastLessonId = typeof lastLesson === 'string' ? lastLesson : lastLesson.id;
                                          selectLesson(lastLessonId);
                                        }
                                      }
                                    }}
                                    disabled={currentLessonIndex === 0 && currentSectionIndex === 0}
                                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                                    Previous
                                  </button>

                                  <span className="text-sm text-gray-500">
                                    Lesson {currentLessonIndex + 1} of {currentSectionData?.lessons?.length || 0}
                                  </span>

                                  <button
                                    onClick={() => {
                                      // Navigate to next lesson
                                      if (currentSectionData && currentLessonIndex < (currentSectionData.lessons?.length || 0) - 1) {
                                        const nextLesson = currentSectionData.lessons[currentLessonIndex + 1];
                                        const nextLessonId = typeof nextLesson === 'string' ? nextLesson : nextLesson.id;
                                        selectLesson(nextLessonId);
                                      } else if (currentSectionIndex < (course.sections?.length || 0) - 1) {
                                        // Navigate to next section's first lesson
                                        const nextSection = course.sections[currentSectionIndex + 1];
                                        if (typeof nextSection !== 'string' && nextSection.lessons && nextSection.lessons.length > 0) {
                                          const firstLesson = nextSection.lessons[0];
                                          const firstLessonId = typeof firstLesson === 'string' ? firstLesson : firstLesson.id;
                                          selectLesson(firstLessonId);
                                        }
                                      }
                                    }}
                                    disabled={currentLessonIndex === (currentSectionData?.lessons?.length || 0) - 1 && currentSectionIndex === (course.sections?.length || 0) - 1}
                                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Next
                                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                                  </button>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>Section {currentSectionIndex + 1} of {course.sections?.length || 0}</span>
                                    <span className="hidden sm:inline">•</span>
                                    <span className="truncate max-w-[150px] sm:max-w-[250px]" title={currentSectionData?.name}>
                                      {currentSectionData?.name || `Section ${currentSectionIndex + 1}`}
                                    </span>
                                  </div>
                                  {currentLesson && (
                                    <button
                                      onClick={() => saveLessonProgress(currentLesson, 100)}
                                      className="inline-flex items-center px-3 py-1.5 rounded-md bg-green-600 text-white text-xs hover:bg-green-700"
                                    >
                                      Mark Complete
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setIsFocusMode(!isFocusMode)}
                                    className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${isFocusMode
                                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                      }`}
                                    title={isFocusMode ? "Show Sidebar" : "Full Screen (Focus Mode)"}
                                  >
                                    {isFocusMode ? (
                                      <>
                                        <Bars3Icon className="h-4 w-4 mr-1.5" />
                                        Show Sidebar
                                      </>
                                    ) : (
                                      <>
                                        <SparklesIcon className="h-4 w-4 mr-1.5" />
                                        Full Screen
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>

                            <LessonContent
                              key={lessonData.id}
                              lesson={lessonData}
                              courseId={courseId || ''}
                              courseName={course.name}
                              courseDescription={course.description}
                              onComplete={() => {
                                if (currentLesson) {
                                  saveLessonProgress(currentLesson, 100);
                                  toast.success('Progress saved');
                                }
                              }}
                              onNext={() => {
                                // Navigate to next lesson
                                if (currentSectionData && currentLessonIndex < (currentSectionData.lessons?.length || 0) - 1) {
                                  const nextLesson = currentSectionData.lessons[currentLessonIndex + 1];
                                  const nextLessonId = typeof nextLesson === 'string' ? nextLesson : nextLesson.id;
                                  if (currentLesson) saveLessonProgress(currentLesson, isLessonCompleted(currentLesson) ? 100 : 100);
                                  selectLesson(nextLessonId);
                                } else if (currentSectionIndex < (course.sections?.length || 0) - 1) {
                                  // Navigate to next section's first lesson
                                  const nextSection = course.sections[currentSectionIndex + 1];
                                  if (typeof nextSection !== 'string' && nextSection.lessons && nextSection.lessons.length > 0) {
                                    const firstLesson = nextSection.lessons[0];
                                    const firstLessonId = typeof firstLesson === 'string' ? firstLesson : firstLesson.id;
                                    if (currentLesson) saveLessonProgress(currentLesson, isLessonCompleted(currentLesson) ? 100 : 100);
                                    selectLesson(firstLessonId);
                                  }
                                }
                              }}
                              onPrevious={() => {
                                // Navigate to previous lesson
                                if (currentSectionData && currentLessonIndex > 0) {
                                  const prevLesson = currentSectionData.lessons[currentLessonIndex - 1];
                                  const prevLessonId = typeof prevLesson === 'string' ? prevLesson : prevLesson.id;
                                  if (currentLesson) saveLessonProgress(currentLesson, Math.max(getLessonProgress(currentLesson)?.progress || 1, 50));
                                  selectLesson(prevLessonId);
                                } else if (currentSectionIndex > 0) {
                                  // Navigate to previous section's last lesson
                                  const prevSection = course.sections[currentSectionIndex - 1];
                                  if (typeof prevSection !== 'string' && prevSection.lessons && prevSection.lessons.length > 0) {
                                    const lastLesson = prevSection.lessons[prevSection.lessons.length - 1];
                                    const lastLessonId = typeof lastLesson === 'string' ? lastLesson : lastLesson.id;
                                    if (currentLesson) saveLessonProgress(currentLesson, Math.max(getLessonProgress(currentLesson)?.progress || 1, 50));
                                    selectLesson(lastLessonId);
                                  }
                                }
                              }}
                            />
                          </>
                        );
                      } else {
                        return (
                          <div className="text-center py-12 text-gray-500">
                            <DocumentTextIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                            <p>Lesson not found</p>
                            <p className="text-sm">Please select a valid lesson from the sidebar</p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <AcademicCapIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome to {getCleanText(course.name)}</h2>
                    <div className="text-gray-600 mb-6">
                      {course.sections && course.sections.length > 0
                        ? 'Select a lesson from the sidebar to start learning'
                        : 'This course is being prepared. Content will be available soon.'
                      }
                    </div>
                    {course.sections && course.sections.length > 0 ? (
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <VideoCameraIcon className="h-4 w-4 mr-1" />
                          <span>Video Lessons</span>
                        </div>
                        <div className="flex items-center">
                          <QuestionMarkCircleIcon className="h-4 w-4 mr-1" />
                          <span>Interactive Quizzes</span>
                        </div>
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-4 w-4 mr-1" />
                          <span>Reading Materials</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">
                        <p>Course content is being developed</p>
                        <p>Check back later for updates</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Mobile bottom navigation for lessons */}
              {currentLesson && (
                <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex items-center justify-between z-20">
                  <button
                    onClick={goToPrevLesson}
                    className="flex-1 mr-2 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gray-100 text-gray-700 disabled:opacity-50 text-sm font-medium"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" /> Previous
                  </button>
                  <button
                    onClick={goToNextLesson}
                    className="flex-1 ml-2 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-primary-600 text-white text-sm font-medium"
                  >
                    Next <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Learning Material Modal */}
      {materialModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 's' || e.key.toLowerCase() === 'p')) {
              e.preventDefault();
            }
          }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={() => setMaterialModal(null)}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-[95%] md:w-4/5 lg:w-3/5 max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm md:text-base font-semibold text-gray-900 truncate">{materialModal.title}</h3>
              <button onClick={() => setMaterialModal(null)} className="text-gray-500 hover:text-gray-700 text-sm">Close</button>
            </div>
            <div className="bg-gray-50">
              <iframe
                src={materialModal.url}
                className="w-full h-[70vh]"
                allow="fullscreen"
                title={materialModal.title}
              />
            </div>
            <div className="px-4 py-2 text-xs text-gray-500 border-t">
              Downloads are disabled for protected materials.
            </div>
          </div>
        </div>
      )}
    </div>


  );
};

export default CourseLearning;